/**
 * `GET /api/config` — the only unauthenticated data the client needs to boot.
 * The server sends `appEnv` (`APP_ENV`) and `theme` (`PORTAL_THEME`); both stay
 * optional here so the client degrades to `VITE_THEME` / no environment badge
 * if a server ever omits them.
 */
export type AppEnv = "local" | "staging" | "production";

export interface PortalConfig {
  supabaseUrl: string | null;
  supabasePublishableKey: string | null;
  authMode: "jwt" | "mock";
  appEnv?: AppEnv;
  theme?: string;
}

export const DEFAULT_CONFIG: PortalConfig = {
  supabaseUrl: null,
  supabasePublishableKey: null,
  authMode: "jwt",
};

function isAppEnv(v: unknown): v is AppEnv {
  return v === "local" || v === "staging" || v === "production";
}

export function normalizeConfig(raw: unknown): PortalConfig {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    supabaseUrl: typeof r.supabaseUrl === "string" && r.supabaseUrl ? r.supabaseUrl : null,
    supabasePublishableKey:
      typeof r.supabasePublishableKey === "string" && r.supabasePublishableKey
        ? r.supabasePublishableKey
        : null,
    authMode: r.authMode === "mock" ? "mock" : "jwt",
    appEnv: isAppEnv(r.appEnv) ? r.appEnv : undefined,
    theme: typeof r.theme === "string" && r.theme.trim() ? r.theme.trim() : undefined,
  };
}

export async function loadPortalConfig(fetchImpl: typeof fetch = fetch): Promise<PortalConfig> {
  const res = await fetchImpl("/api/config", { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`config_http_${res.status}`);
  return normalizeConfig(await res.json());
}
